// AdminRequests — Pending queue with bulk-approve + recently-approved
// slip re-download. Team-scoped to the current outreach tab.

import React, { useState } from "react";
import { Check, ChevronRight, FileText, Inbox, X } from "lucide-react";
import { AMBER, NAVY, TEAL, useInv } from "./constants";
import { SEED_CATALOGUE, SEED_HQ_STOCK, SEED_REQUESTS, SEED_SUPPLIERS, catItem, supplierName, teamLabel } from "./seed";
import { InventoryPdfPreview } from "./PdfPreviewModal";

export function AdminRequests() {
  const {
    requests: REQUESTS = SEED_REQUESTS,
    hqStock: HQ_STOCK = SEED_HQ_STOCK,
    catalogue: CATALOGUE = SEED_CATALOGUE,
    suppliers: SUPPLIERS = SEED_SUPPLIERS,
    activeTeam,
    approveRequest,
    denyRequest,
    approvePO,
    hasToken,
  } = useInv();
  // Scope every admin view here down to the currently-active outreach team
  // (chosen via the Outreach 1 / Outreach 2 tabs above). HQ-level views like
  // Purchase Orders remain shared across teams.
  const teamScoped = (r) => !activeTeam || r.team === activeTeam;
  const pending = REQUESTS.filter((r) => r.status === "pending" && teamScoped(r));
  // Show the 5 most recently approved requests so admin can re-download
  // the pick slip PDF anytime (in case they lost the auto-download).
  const recentApproved = REQUESTS
    .filter((r) => r.status === "approved" && teamScoped(r))
    .sort((a, b) => (b.movedAt_iso || b.at_iso || "").localeCompare(a.movedAt_iso || a.at_iso || ""))
    .slice(0, 5);
  const [busyId, setBusyId] = useState(null);
  // PDF preview state — `{ kind: "slip", payload: {...} }`
  const [pdfPreview, setPdfPreview] = useState(null);
  const [recentOpen, setRecentOpen] = useState(false);

  // Bulk-approve selection state — Set of request IDs.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { groups: [{supplierId, supplierName, items:[{itemId,name,qty}]}] }
  const toggleSel = (rid) => setSelected((s) => {
    const n = new Set(s);
    n.has(rid) ? n.delete(rid) : n.add(rid);
    return n;
  });
  const selectAll = () => setSelected(new Set(pending.map((r) => r.id)));
  const selectNone = () => setSelected(new Set());

  // Group selected requests by the item's supplier so we can create ONE draft
  // PO per supplier rather than N individual POs.
  const buildSupplierGroups = () => {
    const bySup = new Map();
    for (const req of pending) {
      if (!selected.has(req.id)) continue;
      const it = CATALOGUE.find((c) => c.id === req.item);
      if (!it) continue;
      const supId = it.supplier;
      const supName = SUPPLIERS.find((s) => s.id === supId)?.company || "Unknown supplier";
      if (!bySup.has(supId)) bySup.set(supId, { supplierId: supId, supplierName: supName, items: new Map() });
      const g = bySup.get(supId);
      // Combine quantities if the same item shows up across multiple requests
      // (e.g. two teams both requesting the same kit).
      const prev = g.items.get(req.item) || { itemId: req.item, name: it.name, qty: 0, requestIds: [] };
      prev.qty += req.qty;
      prev.requestIds.push(req.id);
      g.items.set(req.item, prev);
    }
    return Array.from(bySup.values()).map((g) => ({
      ...g,
      items: Array.from(g.items.values()),
    }));
  };

  const onApprove = async (req) => {
    if (!approveRequest) return;
    setBusyId(req.id);
    try {
      await approveRequest(req.id);
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const onDeny = async (rid) => {
    if (!denyRequest) return;
    setBusyId(rid);
    try { await denyRequest(rid); } catch (e) { alert(e.message); } finally { setBusyId(null); }
  };

  // Kick off bulk approve — first show a supplier-grouped confirmation.
  const startBulkApprove = () => {
    const groups = buildSupplierGroups();
    if (groups.length === 0) return;
    setBulkConfirm({ groups });
  };

  const runBulkApprove = async () => {
    if (!bulkConfirm || !approveRequest || !approvePO) return;
    setBulkBusy(true);
    try {
      // 1) Approve each selected request (moves HQ → team stock per request).
      const allReqIds = Array.from(selected);
      for (const rid of allReqIds) {
        try { await approveRequest(rid); } catch (e) { console.warn("approve failed", rid, e.message); }
      }
      // 2) Create one draft PO per supplier group (so HQ knows what to reorder).
      for (const g of bulkConfirm.groups) {
        try {
          await approvePO({
            supplier: g.supplierId,
            items: g.items.map((i) => ({ item: i.itemId, qty: i.qty })),
            approvedBy: "Bulk approval",
            note: `Auto-generated draft from bulk approval of ${g.items.reduce((s,i)=>s+i.requestIds.length,0)} request(s).`,
            status: "draft",
          });
        } catch (e) { console.warn("po draft failed", g.supplierId, e.message); }
      }
      setSelected(new Set());
      setBulkConfirm(null);
    } finally { setBulkBusy(false); }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 sm:p-6 shadow-sm">
      <div className="mb-4">
        <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1">
          {hasToken ? (
            "Admin · /users/admin"
          ) : (
            <span data-testid="admin-scope-indicator">
              Submitted orders{activeTeam ? ` - ${teamLabel(activeTeam)}` : ""}
            </span>
          )}
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ color: NAVY }}>
            {hasToken ? "Pending requests" : "Your pending orders"}
          </h2>
          {/* Right-aligned awaiting-count pill so it hits the same
              right-edge as the "Monthly count" toggle on the Staff side. */}
          <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold ml-auto" style={{ background: `${AMBER}1A`, color: "#78350F" }}>
            <Inbox className="h-3.5 w-3.5" /> {pending.length} awaiting
          </span>
        </div>
      </div>

      {/* Bulk-approve toolbar — visible when admin has multiple pending
          items. Selecting groups items by supplier and creates ONE draft
          PO per supplier so procurement stays tidy. */}
      {hasToken && pending.length > 1 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid="bulk-approve-toolbar">
          <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Bulk</span>
          <button
            onClick={selected.size === pending.length ? selectNone : selectAll}
            data-testid="bulk-select-all-btn"
            className="h-7 px-2.5 rounded-md text-xs font-semibold border border-slate-300 bg-white hover:bg-slate-100">
            {selected.size === pending.length ? "Clear selection" : "Select all"}
          </button>
          <span className="text-xs text-slate-600 tabular-nums">
            {selected.size} of {pending.length} selected
          </span>
          <button
            disabled={selected.size === 0}
            onClick={startBulkApprove}
            data-testid="bulk-approve-btn"
            className="h-7 px-3 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-40 ml-auto"
            style={{ background: TEAL }}>
            <Check className="h-3.5 w-3.5 inline mr-1" /> Approve selected &amp; draft POs
          </button>
        </div>
      )}

      <div className="space-y-2">
        {pending.map((req) => {
          const it = catItem(req.item, CATALOGUE) || { name: req.item, min: 0, max: 0, packQty: 1 };
          // HQ stock is stored in UNITS; requests + min/max are in PACKS.
          // Show everything in packs so "1× DBS" against "HQ 2 packs" reads
          // clean — never units-minus-packs nonsense like "199 available".
          const pq        = Number(it.packQty) || 1;
          const hqPacks   = Math.floor((HQ_STOCK[req.item] ?? 0) / pq);
          // "Available" = HQ packs minus everything already pending on
          // this item across every team, so admin sees what's truly free
          // to authorize right now.
          const alreadyPending = REQUESTS
            .filter((r) => r.item === req.item && r.status === "pending")
            .reduce((s, r) => s + r.qty, 0);
          const available = Math.max(0, hqPacks - alreadyPending);
          // Pending packs sitting on OTHER teams' rows — the queue is
          // scoped to the active team tab, so spell out where the
          // "available" number went or it reads as nonsense.
          const otherPending = alreadyPending - req.qty;
          const isSel = selected.has(req.id);
          return (
            <div key={req.id} className={`rounded-md border p-3 transition ${isSel ? "border-[#2A9F92] bg-[#2A9F92]/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center gap-3 flex-wrap">
                {hasToken && pending.length > 1 && (
                  <input
                    type="checkbox"
                    checked={isSel}
                    onChange={() => toggleSel(req.id)}
                    data-testid={`bulk-select-req-${req.id}`}
                    className="h-4 w-4 rounded border-slate-300 accent-[#2A9F92] cursor-pointer shrink-0"
                    aria-label={`Select ${it.name} for bulk approval`}
                  />
                )}
                <div className="flex-1 min-w-[180px]">
                  <p className="font-semibold text-sm" style={{ color: NAVY }}>
                    <span className="tabular-nums font-semibold text-base mr-1" style={{ color: TEAL }}>{req.qty}×</span>
                    {it.name}
                  </p>
                  {hasToken && (
                    <>
                      <p className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
                        HQ {hqPacks} pack{hqPacks === 1 ? "" : "s"} · {available} available · Min {it.min}/{it.max}
                      </p>
                      {otherPending > 0 && (
                        <p className="text-[11px] text-amber-700 tabular-nums" data-testid={`other-teams-pending-${req.id}`}>
                          {otherPending} pack{otherPending === 1 ? "" : "s"} pending on other teams
                        </p>
                      )}
                    </>
                  )}
                </div>

                <div className="flex flex-nowrap items-center gap-1.5 justify-end shrink-0">
                  {/* Team badge sized to match the action buttons.
                      Uses a short name on mobile ("George") and the full
                      label ("Outreach (George)") on tablet/desktop so
                      the three-button row never wraps or overflows. */}
                  <span className="inline-flex items-center h-8 px-3 rounded-md text-xs font-semibold whitespace-nowrap" style={{ background: `${NAVY}12`, color: NAVY }}>
                    {/* Extract just the name in the parens on mobile,
                        e.g. "Outreach (George)" → "George". Falls back
                        to the raw label when parens are missing. */}
                    <span className="sm:hidden">
                      {(() => {
                        const full = teamLabel(req.team);
                        const m = full.match(/\(([^)]+)\)/);
                        return m ? m[1] : full;
                      })()}
                    </span>
                    <span className="hidden sm:inline">{teamLabel(req.team)}</span>
                  </span>
                  {hasToken && (
                    <>
                      <button
                        disabled={busyId === req.id}
                        onClick={() => onApprove(req)}
                        data-testid={`approve-req-${req.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50" style={{ background: TEAL }}>
                        <Check className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button
                        disabled={busyId === req.id}
                        onClick={() => onDeny(req.id)}
                        data-testid={`deny-req-${req.id}`}
                        className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-semibold text-red-600 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50">
                        <X className="h-3.5 w-3.5" /> Deny
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {pending.length === 0 && (
          <div className="py-10 text-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-md">
            No pending requests. Fresh queue.
          </div>
        )}
      </div>

      {/* Bulk-approve confirm modal — grouped by supplier. */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/60 p-4" data-testid="bulk-confirm-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-5 border-b border-slate-200">
              <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">Bulk approval</p>
              <h3 className="text-lg font-semibold mt-1" style={{ color: NAVY }}>
                Approve {selected.size} request{selected.size === 1 ? "" : "s"} &amp; create {bulkConfirm.groups.length} draft PO{bulkConfirm.groups.length === 1 ? "" : "s"}
              </h3>
              <p className="text-xs text-slate-600 mt-1">
                Stock will move HQ → team for each request. One draft purchase order will be created per supplier so you can review, edit and send them later.
              </p>
            </div>
            <div className="max-h-72 overflow-y-auto p-5 space-y-3">
              {bulkConfirm.groups.map((g) => (
                <div key={g.supplierId} className="rounded-md border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: TEAL }}>Draft PO · {g.supplierName}</p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {g.items.map((i) => (
                      <li key={i.itemId} className="flex justify-between gap-2">
                        <span style={{ color: NAVY }}>{i.name}</span>
                        <span className="tabular-nums text-slate-700 font-semibold">{i.qty}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={() => setBulkConfirm(null)}
                disabled={bulkBusy}
                data-testid="bulk-confirm-cancel-btn"
                className="h-9 px-4 rounded-md text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={runBulkApprove}
                disabled={bulkBusy}
                data-testid="bulk-confirm-run-btn"
                className="h-9 px-4 rounded-md text-xs font-semibold text-white shadow-sm disabled:opacity-50"
                style={{ background: TEAL }}>
                {bulkBusy ? "Approving…" : "Approve & create drafts"}
              </button>
            </div>
          </div>
        </div>
      )}

      {recentApproved.length > 0 && (() => {
        // Group approved requests by (team + approval date, YYYY-MM-DD) so
        // a single combined pick slip covers everything released together.
        const groups = new Map();
        for (const req of recentApproved) {
          const iso = req.movedAt_iso || req.at_iso || "";
          const dayKey = iso ? iso.slice(0, 10) : "unknown";
          const key = `${req.team}__${dayKey}`;
          if (!groups.has(key)) groups.set(key, { team: req.team, dayKey, items: [], mostRecent: iso });
          const g = groups.get(key);
          g.items.push(req);
          if (iso > (g.mostRecent || "")) g.mostRecent = iso;
        }
        const groupList = Array.from(groups.values())
          .sort((a, b) => (b.mostRecent || "").localeCompare(a.mostRecent || ""));

        return (
        <div className="mt-6">
          <button
            onClick={() => setRecentOpen((v) => !v)}
            data-testid="toggle-recent-approved-btn"
            className="w-full flex items-center justify-between gap-2 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition">
            <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Approved · pick slips</span>
            <span className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: NAVY }}>
              {groupList.length}
              {recentOpen ? <ChevronRight className="h-4 w-4 rotate-90" /> : <ChevronRight className="h-4 w-4" />}
            </span>
          </button>
          {recentOpen && (
            <div className="mt-2 space-y-2">
              {groupList.map((g) => {
                const dateLabel = g.mostRecent
                  ? new Date(g.mostRecent).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                  : "—";
                const approvedBy = g.items[0]?.approvedBy || "HQ";
                return (
                  <div key={`${g.team}-${g.dayKey}`} className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 flex-nowrap">
                    <p className="flex-1 min-w-0 truncate text-sm font-semibold" style={{ color: NAVY }}>
                      <span className="tabular-nums font-semibold text-base mr-1" style={{ color: TEAL }}>{g.items.length}×</span>
                      item{g.items.length === 1 ? "" : "s"} · <span className="tabular-nums">{dateLabel}</span>
                    </p>
                    <span className="shrink-0 inline-flex items-center h-7 px-2 rounded-md text-[11px] font-semibold whitespace-nowrap" style={{ background: `${NAVY}12`, color: NAVY }}>
                      {teamLabel(g.team)}
                    </span>
                    <button
                      onClick={() => setPdfPreview({
                        kind: "slip",
                        payload: {
                          team: g.team,
                          teamLabel: teamLabel(g.team),
                          approvedBy,
                          dateLabel,
                          items: g.items.map((r) => ({
                            item: catItem(r.item, CATALOGUE) || { name: r.item, code: "", pack: "Each", packQty: 1 },
                            qty: r.qty,
                            requestId: r.id,
                            reason: r.reason || "",
                          })),
                        },
                      })}
                      aria-label="Open Pick slip PDF"
                      data-testid={`slip-pdf-${g.team}-${g.dayKey}`}
                      className="shrink-0 inline-flex items-center justify-center gap-1.5 h-8 w-8 sm:w-auto sm:px-3 rounded-md text-xs font-semibold text-white shadow-sm" style={{ background: TEAL }}>
                      <FileText className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Pick slip PDF</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        );
      })()}

      <InventoryPdfPreview
        open={!!pdfPreview}
        kind={pdfPreview?.kind}
        payload={pdfPreview?.payload}
        onClose={() => setPdfPreview(null)}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────
// 3. Admin — HQ Receiving (log incoming supplier deliveries)
// ─────────────────────────────────────────────────────────────────

